import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#E2E8E4] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#1D6B2A] focus:ring-4 focus:ring-[#4CAF50]/15 transition-all";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("blog");
  const [posts, setPosts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", category: "Insights", cover_image: "", author: "Chennai Packs Team" });

  const load = () => {
    api.blog().then(setPosts);
    api.adminQuotes().then(setQuotes).catch(() => {});
  };

  useEffect(() => {
    if (!user) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.adminBlogCreate(form);
      toast.success("Post published!");
      setForm({ title: "", excerpt: "", content: "", category: "Insights", cover_image: "", author: "Chennai Packs Team" });
      load();
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate("/admin/login"); }
      toast.error("Could not publish post.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.adminBlogDelete(id);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Could not delete post.");
    }
  };

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-head text-3xl font-semibold text-[#0A1C0E]">Admin Dashboard</h1>
          <p className="text-[#3A4D3E] text-sm">{user?.email}</p>
        </div>
        <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 border border-[#E2E8E4] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#F8FAF8]">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div className="flex gap-2 mb-8">
        {["blog", "quotes"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`px-5 py-2.5 rounded-full text-sm font-medium capitalize transition-colors ${tab === t ? "bg-[#1D6B2A] text-white" : "text-[#3A4D3E] hover:bg-[#F8FAF8]"}`}>
            {t === "blog" ? "Blog Posts" : "Quote Requests"}
          </button>
        ))}
      </div>

      {tab === "blog" && (
        <div className="grid lg:grid-cols-12 gap-8">
          <form onSubmit={create} data-testid="admin-blog-form" className="lg:col-span-5 bg-white border border-[#E2E8E4] rounded-2xl p-6 space-y-3 h-fit">
            <h2 className="font-head text-lg font-semibold text-[#0A1C0E] flex items-center gap-2"><Plus size={18} /> New Post</h2>
            <input required placeholder="Title" value={form.title} onChange={set("title")} className={inputCls} data-testid="blog-title" />
            <input placeholder="Category" value={form.category} onChange={set("category")} className={inputCls} data-testid="blog-category" />
            <input placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} className={inputCls} data-testid="blog-cover" />
            <textarea required rows={2} placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")} className={inputCls} data-testid="blog-excerpt" />
            <textarea required rows={6} placeholder="Content (use blank lines between paragraphs)" value={form.content} onChange={set("content")} className={inputCls} data-testid="blog-content" />
            <button type="submit" disabled={saving} data-testid="blog-publish"
              className="w-full bg-[#1D6B2A] text-white rounded-full px-6 py-3 font-semibold hover:bg-[#154d1e] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Publish Post
            </button>
          </form>

          <div className="lg:col-span-7 space-y-4">
            <h2 className="font-head text-lg font-semibold text-[#0A1C0E]">Published ({posts.length})</h2>
            {posts.map((p) => (
              <div key={p.id} className="bg-white border border-[#E2E8E4] rounded-2xl p-4 flex gap-4 items-center" data-testid={`admin-post-${p.id}`}>
                {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <h3 className="font-head font-semibold text-[#0A1C0E] truncate">{p.title}</h3>
                  <p className="text-xs text-[#3A4D3E]">{p.category}</p>
                </div>
                <button onClick={() => remove(p.id)} data-testid={`delete-post-${p.id}`} className="grid place-items-center w-10 h-10 rounded-full text-red-500 hover:bg-red-50">
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "quotes" && (
        <div className="space-y-4">
          <h2 className="font-head text-lg font-semibold text-[#0A1C0E]">Quote Requests ({quotes.length})</h2>
          {quotes.length === 0 && <p className="text-[#3A4D3E]">No quote requests yet.</p>}
          {quotes.map((q) => (
            <div key={q.id} className="bg-white border border-[#E2E8E4] rounded-2xl p-5" data-testid={`quote-row-${q.id}`}>
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-head font-semibold text-[#0A1C0E]">{q.name} {q.company && `· ${q.company}`}</span>
                <span className="text-xs text-[#3A4D3E]">{new Date(q.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-[#3A4D3E] mt-1">{q.email} · {q.phone} · {q.city}</p>
              <p className="text-sm text-[#0A1C0E] mt-2">
                <b>{q.product}</b> {q.quantity && `— ${q.quantity}`} {q.business_type && `(${q.business_type})`}
              </p>
              {q.message && <p className="text-sm text-[#3A4D3E] mt-1 italic">&ldquo;{q.message}&rdquo;</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
